import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ArrowLeft, Upload, FileText, AlertTriangle, AlertCircle,
  CheckCircle, ChevronDown, ChevronRight, Scale, Lightbulb, PenTool,
  Layers, Copy, Loader2, Search, Filter, Info, X, FileUp, Clipboard,
  Target, BookOpen, Sparkles, Eye, Download, Save, Clock, ArrowLeftRight,
  ShieldCheck, FileWarning, Wand2, RefreshCw, Check, Plus, AlertOctagon,
  Brain, Zap, FileCheck, CircleDot, History
} from 'lucide-react';

const RISK_LEVEL_CONFIG = {
  bajo: { color: 'green', bgClass: 'bg-green-500/20', textClass: 'text-green-400', icon: CheckCircle },
  moderado: { color: 'yellow', bgClass: 'bg-yellow-500/20', textClass: 'text-yellow-400', icon: AlertCircle },
  alto: { color: 'orange', bgClass: 'bg-orange-500/20', textClass: 'text-orange-400', icon: AlertTriangle },
  crítico: { color: 'red', bgClass: 'bg-red-500/20', textClass: 'text-red-400', icon: AlertOctagon }
};

const SEVERITY_CONFIG = {
  crítica: { color: 'red', bgClass: 'bg-red-500/20', textClass: 'text-red-400' },
  alta: { color: 'orange', bgClass: 'bg-orange-500/20', textClass: 'text-orange-400' },
  media: { color: 'yellow', bgClass: 'bg-yellow-500/20', textClass: 'text-yellow-400' },
  baja: { color: 'blue', bgClass: 'bg-blue-500/20', textClass: 'text-blue-400' }
};

const CONTRACT_TYPES = [
  { id: "arriendo", label: "Arriendo" },
  { id: "compraventa", label: "Compraventa" },
  { id: "laboral", label: "Laboral" },
  { id: "servicios", label: "Prestación de Servicios" },
  { id: "general", label: "Otro / General" }
];

const USER_ROLES = {
  arriendo: [
    { id: "arrendador", label: "Arrendador (Dueño)" },
    { id: "arrendatario", label: "Arrendatario (Inquilino)" }
  ],
  compraventa: [
    { id: "vendedor", label: "Vendedor" },
    { id: "comprador", label: "Comprador" }
  ],
  laboral: [
    { id: "empleador", label: "Empleador" },
    { id: "trabajador", label: "Trabajador" }
  ],
  servicios: [
    { id: "prestador", label: "Prestador de Servicios" },
    { id: "mandante", label: "Mandante / Cliente" }
  ],
  general: [
    { id: "parte_a", label: "Primera Parte" },
    { id: "parte_b", label: "Segunda Parte" }
  ]
};

const ContractProtectionPage = () => {
  const { api } = useAuth();
  
  // Input state
  const [contractContent, setContractContent] = useState('');
  const [contractType, setContractType] = useState('');
  const [userRole, setUserRole] = useState('');
  const [fileName, setFileName] = useState('');
  
  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  
  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1: Input, 2: Risks, 3: Clauses, 4: Improved
  
  // Clause selection
  const [selectedClauses, setSelectedClauses] = useState(new Set());
  const [selectedModifications, setSelectedModifications] = useState(new Set());
  
  // Generating improved contract
  const [generatingImproved, setGeneratingImproved] = useState(false);
  const [improvedContract, setImprovedContract] = useState(null);
  
  // Expanded items
  const [expandedRisk, setExpandedRisk] = useState(null);
  const [expandedClause, setExpandedClause] = useState(null);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.txt')) {
      toast.error('Por favor suba un archivo de texto (.txt)');
      return;
    }

    try {
      const text = await file.text();
      setContractContent(text);
      setFileName(file.name);
      toast.success(`Archivo "${file.name}" cargado correctamente`);
    } catch (error) {
      toast.error('Error al leer el archivo');
    }
  }, []);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setContractContent(text);
      toast.success('Texto pegado desde el portapapeles');
    } catch (error) {
      toast.error('No se pudo acceder al portapapeles');
    }
  }, []);

  const analyzeContract = async () => {
    if (!contractContent.trim()) {
      toast.error('Por favor ingrese el contenido del contrato');
      return;
    }

    if (contractContent.length < 100) {
      toast.error('El contrato es demasiado corto para analizar');
      return;
    }

    setAnalyzing(true);
    setAnalysis(null);

    try {
      const response = await api.post('/contract-protection/full-analysis', {
        content: contractContent,
        contract_type: contractType || 'general',
        user_role: userRole || null,
        auto_improve: false
      });

      setAnalysis(response.data);
      setCurrentStep(2);
      toast.success('Análisis completado');
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Error al analizar el contrato');
    } finally {
      setAnalyzing(false);
    }
  };

  const generateImprovedContract = async () => {
    if (!analysis) return;

    setGeneratingImproved(true);

    try {
      // Get selected clause objects
      const clausesToApply = analysis.clause_suggestions?.protective_clauses?.filter(
        (_, idx) => selectedClauses.has(idx)
      ) || [];
      
      const modificationsToApply = analysis.clause_suggestions?.modifications_suggested?.filter(
        (_, idx) => selectedModifications.has(idx)
      ) || [];

      const response = await api.post('/contract-protection/generate-improved', {
        content: contractContent,
        contract_type: contractType || 'general',
        user_role: userRole || null,
        selected_clause_ids: clausesToApply.map(c => c.id),
        selected_modifications: modificationsToApply
      });

      setImprovedContract(response.data);
      setCurrentStep(4);
      toast.success('Contrato mejorado generado exitosamente');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Error al generar contrato mejorado');
    } finally {
      setGeneratingImproved(false);
    }
  };

  const toggleClauseSelection = (idx) => {
    const newSet = new Set(selectedClauses);
    if (newSet.has(idx)) {
      newSet.delete(idx);
    } else {
      newSet.add(idx);
    }
    setSelectedClauses(newSet);
  };

  const toggleModificationSelection = (idx) => {
    const newSet = new Set(selectedModifications);
    if (newSet.has(idx)) {
      newSet.delete(idx);
    } else {
      newSet.add(idx);
    }
    setSelectedModifications(newSet);
  };

  const selectAllClauses = () => {
    const allIds = new Set(
      analysis?.clause_suggestions?.protective_clauses?.map((_, idx) => idx) || []
    );
    setSelectedClauses(allIds);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const downloadImprovedContract = () => {
    if (!improvedContract?.improved_contract) return;
    
    const blob = new Blob([improvedContract.improved_contract], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contrato_mejorado_${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Contrato descargado');
  };

  const resetAll = () => {
    setAnalysis(null);
    setContractContent('');
    setContractType('');
    setUserRole('');
    setFileName('');
    setCurrentStep(1);
    setSelectedClauses(new Set());
    setSelectedModifications(new Set());
    setImprovedContract(null);
  };

  const getRiskGradient = (level) => {
    switch(level) {
      case 'crítico': return 'from-red-600 to-red-700';
      case 'alto': return 'from-orange-500 to-orange-600';
      case 'moderado': return 'from-yellow-500 to-yellow-600';
      default: return 'from-green-500 to-green-600';
    }
  };

  // Render steps indicator
  const renderStepsIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[
        { num: 1, label: 'Contrato', icon: FileText },
        { num: 2, label: 'Riesgos', icon: AlertTriangle },
        { num: 3, label: 'Cláusulas', icon: ShieldCheck },
        { num: 4, label: 'Mejorado', icon: FileCheck }
      ].map((step, idx) => {
        const Icon = step.icon;
        const isActive = currentStep === step.num;
        const isCompleted = currentStep > step.num;
        
        return (
          <React.Fragment key={step.num}>
            {idx > 0 && (
              <div className={`w-12 h-0.5 ${isCompleted ? 'bg-emerald-500' : 'bg-slate-700'}`} />
            )}
            <button
              onClick={() => step.num < currentStep && setCurrentStep(step.num)}
              disabled={step.num > currentStep}
              className={`flex flex-col items-center gap-1 transition-all ${
                step.num <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isActive 
                  ? 'bg-emerald-500 text-white' 
                  : isCompleted 
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500' 
                    : 'bg-slate-800 text-slate-500 border border-slate-700'
              }`}>
                {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs ${isActive ? 'text-emerald-400' : 'text-slate-500'}`}>
                {step.label}
              </span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/dashboard" 
              className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-emerald-500" />
              <span className="font-semibold font-['Outfit']">Protección de Contratos IA</span>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/historial-proteccion"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors"
                data-testid="history-link"
              >
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Historial</span>
              </Link>
              <Link
                to="/comparar-contratos"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                data-testid="compare-contracts-link"
              >
                <ArrowLeftRight className="w-4 h-4" />
                <span className="hidden sm:inline">Comparar</span>
              </Link>
              <Link
                to="/analizar-contrato"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Análisis Simple</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {renderStepsIndicator()}

        {/* Step 1: Input Contract */}
        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold font-['Outfit'] mb-3">
                Protege tu Contrato con IA
              </h1>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Sube o pega un contrato externo. Nuestra IA analizará los riesgos, 
                sugerirá cláusulas protectoras y generará una versión mejorada con tu aprobación.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Input */}
              <div className="lg:col-span-2 space-y-4">
                <div className="relative">
                  <textarea
                    value={contractContent}
                    onChange={(e) => setContractContent(e.target.value)}
                    placeholder="Pega aquí el contenido del contrato que deseas analizar y proteger...

El sistema identificará:
• Riesgos legales y cláusulas problemáticas
• Elementos faltantes importantes
• Mejoras de redacción y estructura

Y te sugerirá:
• Cláusulas protectoras específicas para tu rol
• Modificaciones a cláusulas existentes
• Una versión mejorada del contrato"
                    className="w-full h-80 p-4 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:border-emerald-500 font-mono text-sm"
                    data-testid="contract-content-input"
                  />
                  <div className="absolute bottom-3 right-3 flex gap-2">
                    <button
                      onClick={handlePaste}
                      className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1.5"
                    >
                      <Clipboard className="w-4 h-4" />
                      Pegar
                    </button>
                    <label className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1.5 cursor-pointer">
                      <FileUp className="w-4 h-4" />
                      Subir
                      <input
                        type="file"
                        accept=".txt"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                    {contractContent && (
                      <button
                        onClick={() => setContractContent('')}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-red-600/50 rounded-lg text-sm flex items-center gap-1.5"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {fileName && (
                  <p className="text-sm text-emerald-400 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Archivo cargado: {fileName}
                  </p>
                )}

                <p className="text-sm text-slate-500">
                  {contractContent.length.toLocaleString()} caracteres
                </p>
              </div>

              {/* Options */}
              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-400" />
                    Configuración
                  </h3>

                  {/* Contract Type */}
                  <div className="mb-4">
                    <label className="text-sm text-slate-400 block mb-2">
                      Tipo de Contrato
                    </label>
                    <select
                      value={contractType}
                      onChange={(e) => { setContractType(e.target.value); setUserRole(''); }}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                      data-testid="contract-type-select"
                    >
                      <option value="">Detectar automáticamente</option>
                      {CONTRACT_TYPES.map(ct => (
                        <option key={ct.id} value={ct.id}>{ct.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* User Role */}
                  {contractType && USER_ROLES[contractType] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mb-4"
                    >
                      <label className="text-sm text-slate-400 block mb-2">
                        Tu Rol en el Contrato
                      </label>
                      <select
                        value={userRole}
                        onChange={(e) => setUserRole(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                        data-testid="user-role-select"
                      >
                        <option value="">Seleccionar rol</option>
                        {USER_ROLES[contractType].map(role => (
                          <option key={role.id} value={role.id}>{role.label}</option>
                        ))}
                      </select>
                    </motion.div>
                  )}

                  {/* Analyze Button */}
                  <button
                    onClick={analyzeContract}
                    disabled={analyzing || !contractContent.trim()}
                    className="w-full py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="analyze-contract-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analizando con IA...
                      </>
                    ) : (
                      <>
                        <Brain className="w-5 h-5" />
                        Analizar y Proteger
                      </>
                    )}
                  </button>
                </div>

                {/* Features Info */}
                <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-xl p-5">
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-emerald-300">
                    <Zap className="w-5 h-5" />
                    Análisis Potenciado por IA
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 text-orange-400 flex-shrink-0" />
                      <span>Identificación profunda de riesgos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-400 flex-shrink-0" />
                      <span>Cláusulas protectoras personalizadas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Wand2 className="w-4 h-4 mt-0.5 text-purple-400 flex-shrink-0" />
                      <span>Generación de contrato mejorado</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Scale className="w-4 h-4 mt-0.5 text-amber-400 flex-shrink-0" />
                      <span>Base legal chilena actualizada</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Risk Analysis Results */}
        {currentStep === 2 && analysis && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Risk Score Card */}
            <div className={`bg-gradient-to-r ${getRiskGradient(analysis.risk_analysis?.risk_level)} rounded-xl p-6 mb-6`}>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <h2 className="text-2xl font-bold text-white mb-1">
                    Nivel de Riesgo: {analysis.risk_analysis?.risk_level?.toUpperCase()}
                  </h2>
                  <p className="text-white/80">
                    {analysis.risk_analysis?.overall_assessment?.slice(0, 150)}...
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-white">
                    {analysis.risk_analysis?.risk_score || 0}
                  </div>
                  <div className="text-white/80 text-sm">Puntuación</div>
                </div>
              </div>
            </div>

            {/* Risks List */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Riesgos Identificados ({analysis.risk_analysis?.risks?.length || 0})
              </h3>
              
              <div className="space-y-3">
                {analysis.risk_analysis?.risks?.map((risk, idx) => {
                  const severityConfig = SEVERITY_CONFIG[risk.severity] || SEVERITY_CONFIG.media;
                  const isExpanded = expandedRisk === idx;
                  
                  return (
                    <div
                      key={idx}
                      className={`bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden`}
                    >
                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedRisk(isExpanded ? null : idx)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${severityConfig.bgClass}`}>
                            <AlertTriangle className={`w-4 h-4 ${severityConfig.textClass}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${severityConfig.bgClass} ${severityConfig.textClass}`}>
                                {risk.severity?.toUpperCase()}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                                {risk.category}
                              </span>
                            </div>
                            <h4 className="font-semibold">{risk.title}</h4>
                            <p className="text-sm text-slate-400 mt-1">{risk.description?.slice(0, 150)}...</p>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="border-t border-slate-700"
                          >
                            <div className="p-4 space-y-3 bg-slate-900/50">
                              {risk.affected_clause && (
                                <div>
                                  <span className="text-sm font-medium text-red-400">Cláusula Afectada:</span>
                                  <div className="mt-1 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-200">
                                    {risk.affected_clause}
                                  </div>
                                </div>
                              )}
                              <div>
                                <span className="text-sm font-medium text-emerald-400">Recomendación:</span>
                                <p className="mt-1 text-sm text-slate-300">{risk.recommendation}</p>
                              </div>
                              {risk.legal_basis && (
                                <div className="flex items-start gap-2 pt-2 border-t border-slate-700">
                                  <Scale className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                  <p className="text-sm text-amber-200/80">
                                    <strong>Base Legal:</strong> {risk.legal_basis}
                                  </p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Missing Elements */}
            {analysis.risk_analysis?.missing_elements?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FileWarning className="w-5 h-5 text-yellow-400" />
                  Elementos Faltantes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {analysis.risk_analysis.missing_elements.map((elem, idx) => (
                    <div key={idx} className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          elem.importance === 'esencial' ? 'bg-red-500/20 text-red-400' :
                          elem.importance === 'recomendado' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {elem.importance}
                        </span>
                      </div>
                      <h4 className="font-medium text-yellow-200">{elem.element}</h4>
                      <p className="text-sm text-slate-400 mt-1">{elem.suggestion}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              <button
                onClick={resetAll}
                className="px-4 py-2 border border-slate-700 rounded-lg hover:bg-slate-800 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Nuevo Análisis
              </button>
              <button
                onClick={() => setCurrentStep(3)}
                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium flex items-center gap-2"
                data-testid="continue-to-clauses"
              >
                Ver Cláusulas Protectoras
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Clause Suggestions */}
        {currentStep === 3 && analysis && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
                Cláusulas Protectoras Sugeridas
              </h2>
              <button
                onClick={selectAllClauses}
                className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
              >
                <Check className="w-4 h-4" />
                Seleccionar Todas
              </button>
            </div>

            <p className="text-slate-400 mb-6">
              Selecciona las cláusulas que deseas incorporar a tu contrato mejorado.
              {userRole && ` Estas cláusulas están optimizadas para proteger tus intereses como ${userRole}.`}
            </p>

            {/* Protective Clauses */}
            <div className="space-y-4 mb-8">
              {analysis.clause_suggestions?.protective_clauses?.map((clause, idx) => {
                const isSelected = selectedClauses.has(idx);
                const isExpanded = expandedClause === idx;
                
                return (
                  <div
                    key={idx}
                    className={`bg-slate-800/50 rounded-xl border transition-all ${
                      isSelected ? 'border-emerald-500' : 'border-slate-700'
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => toggleClauseSelection(idx)}
                          className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected 
                              ? 'bg-emerald-500 text-white' 
                              : 'border border-slate-600 hover:border-emerald-500'
                          }`}
                          data-testid={`select-clause-${idx}`}
                        >
                          {isSelected && <Check className="w-4 h-4" />}
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              clause.priority === 'alta' ? 'bg-red-500/20 text-red-400' :
                              clause.priority === 'media' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              Prioridad {clause.priority}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                              {clause.category}
                            </span>
                          </div>
                          <h4 className="font-semibold">{clause.title}</h4>
                          <p className="text-sm text-slate-400 mt-1">{clause.purpose}</p>
                        </div>
                        <button
                          onClick={() => setExpandedClause(isExpanded ? null : idx)}
                          className="text-slate-400 hover:text-white"
                        >
                          <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-slate-700"
                        >
                          <div className="p-4 space-y-3 bg-slate-900/50">
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-emerald-400">Texto de la Cláusula:</span>
                                <button
                                  onClick={() => copyToClipboard(clause.full_text)}
                                  className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                                >
                                  <Copy className="w-3 h-3" /> Copiar
                                </button>
                              </div>
                              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-emerald-200 whitespace-pre-wrap">
                                {clause.full_text}
                              </div>
                            </div>
                            {clause.protects_against && (
                              <p className="text-sm text-slate-400">
                                <strong className="text-slate-300">Protege contra:</strong> {clause.protects_against}
                              </p>
                            )}
                            {clause.legal_basis && (
                              <div className="flex items-start gap-2 pt-2 border-t border-slate-700">
                                <Scale className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-amber-200/80">
                                  <strong>Base Legal:</strong> {clause.legal_basis}
                                </p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* Modifications Suggested */}
            {analysis.clause_suggestions?.modifications_suggested?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-purple-400" />
                  Modificaciones Sugeridas
                </h3>
                <div className="space-y-3">
                  {analysis.clause_suggestions.modifications_suggested.map((mod, idx) => {
                    const isSelected = selectedModifications.has(idx);
                    
                    return (
                      <div
                        key={idx}
                        className={`p-4 bg-slate-800/50 rounded-xl border transition-all ${
                          isSelected ? 'border-purple-500' : 'border-slate-700'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => toggleModificationSelection(idx)}
                            className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                              isSelected 
                                ? 'bg-purple-500 text-white' 
                                : 'border border-slate-600 hover:border-purple-500'
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4" />}
                          </button>
                          <div className="flex-1 space-y-2">
                            <p className="text-sm text-slate-400">{mod.reason}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <span className="text-xs text-red-400 block mb-1">Original:</span>
                                <p className="text-sm text-red-200">{mod.original_text}</p>
                              </div>
                              <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                                <span className="text-xs text-emerald-400 block mb-1">Sugerido:</span>
                                <p className="text-sm text-emerald-200">{mod.suggested_text}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selection Summary */}
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-emerald-300">Resumen de Selección</h4>
                  <p className="text-sm text-slate-400">
                    {selectedClauses.size} cláusulas y {selectedModifications.size} modificaciones seleccionadas
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-xs px-2 py-1 rounded ${
                    selectedClauses.size > 3 ? 'bg-emerald-500/20 text-emerald-400' :
                    selectedClauses.size > 0 ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-slate-700 text-slate-400'
                  }`}>
                    Protección: {selectedClauses.size > 3 ? 'Avanzada' : selectedClauses.size > 0 ? 'Básica' : 'Sin protección'}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-4 py-2 border border-slate-700 rounded-lg hover:bg-slate-800 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Ver Riesgos
              </button>
              <button
                onClick={generateImprovedContract}
                disabled={generatingImproved || (selectedClauses.size === 0 && selectedModifications.size === 0)}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-emerald-600 hover:from-purple-700 hover:to-emerald-700 text-white rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="generate-improved-btn"
              >
                {generatingImproved ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Generar Contrato Mejorado
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Improved Contract */}
        {currentStep === 4 && improvedContract && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileCheck className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2">¡Contrato Mejorado Generado!</h2>
              <p className="text-slate-400">
                Nivel de Protección: <span className={`font-semibold ${
                  improvedContract.protection_level === 'avanzado' ? 'text-emerald-400' :
                  improvedContract.protection_level === 'mejorado' ? 'text-blue-400' :
                  'text-yellow-400'
                }`}>{improvedContract.protection_level?.toUpperCase()}</span>
              </p>
            </div>

            {/* Changes Summary */}
            {improvedContract.changes_summary?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-blue-400" />
                  Cambios Realizados
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {improvedContract.changes_summary.map((change, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${
                        change.type === 'agregado' ? 'bg-emerald-500/10 border-emerald-500/30' :
                        change.type === 'modificado' ? 'bg-blue-500/10 border-blue-500/30' :
                        'bg-red-500/10 border-red-500/30'
                      }`}
                    >
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        change.type === 'agregado' ? 'bg-emerald-500/20 text-emerald-400' :
                        change.type === 'modificado' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {change.type}
                      </span>
                      <p className="text-sm mt-2">{change.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Improved Contract Content */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-400" />
                  Contrato Mejorado
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(improvedContract.improved_contract)}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-1.5"
                  >
                    <Copy className="w-4 h-4" />
                    Copiar
                  </button>
                  <button
                    onClick={downloadImprovedContract}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm flex items-center gap-1.5"
                    data-testid="download-improved-btn"
                  >
                    <Download className="w-4 h-4" />
                    Descargar
                  </button>
                </div>
              </div>
              <div className="p-4 bg-slate-900 border border-slate-700 rounded-xl max-h-[500px] overflow-y-auto">
                <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
                  {improvedContract.improved_contract}
                </pre>
              </div>
            </div>

            {/* Legal Notes */}
            {improvedContract.legal_compliance_notes && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl mb-6">
                <div className="flex items-start gap-2">
                  <Scale className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-300">Notas de Cumplimiento Legal</h4>
                    <p className="text-sm text-amber-200/80 mt-1">{improvedContract.legal_compliance_notes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={resetAll}
                className="px-6 py-3 border border-slate-700 rounded-xl hover:bg-slate-800 flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Analizar Otro Contrato
              </button>
              <button
                onClick={() => setCurrentStep(3)}
                className="px-6 py-3 border border-purple-500 text-purple-400 rounded-xl hover:bg-purple-500/10 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Modificar Selección
              </button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ContractProtectionPage;
